import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { CustomerProvider } from "@/context/CustomerContext";
import ScrollToTop from "@/components/ScrollToTop";
import PageTracker from "@/components/PageTracker";
import RouteFallback from "@/components/RouteFallback";
// Home stays eager so the landing page paints without an extra chunk round-trip.
import Home from "@/pages/Home";
import "@/App.css";

// Retail routes (lazy-loaded chunks)
const Catalog = lazy(() => import("@/pages/Catalog"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const ScheduleCall = lazy(() => import("@/pages/ScheduleCall"));
const CustomerLogin = lazy(() => import("@/pages/CustomerLogin"));
const Account = lazy(() => import("@/pages/Account"));

// Admin routes (lazy-loaded chunks)
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminProductUpload = lazy(() => import("@/pages/AdminProductUpload"));
const AdminProductEdit = lazy(() => import("@/pages/AdminProductEdit"));
const AdminVideoBookings = lazy(() => import("@/pages/AdminVideoBookings"));
const AdminCustomers = lazy(() => import("@/pages/AdminCustomers"));
const AdminAnalytics = lazy(() => import("@/pages/AdminAnalytics"));
const AdminOverview = lazy(() => import("@/pages/AdminOverview"));

// B2B Wholesale module (hidden from retail; accessed via private /b2b links) — lazy-loaded.
const B2BWholesaleCatalog = lazy(() => import("@/b2b/pages/Catalog"));
const B2BWholesaleProduct = lazy(() => import("@/b2b/pages/Product"));
const B2BCart = lazy(() => import("@/b2b/pages/Cart"));
const B2BBulkOrder = lazy(() => import("@/b2b/pages/BulkOrder"));
const B2BBuyerInfo = lazy(() => import("@/b2b/pages/BuyerInfo"));
const B2BSuccess = lazy(() => import("@/b2b/pages/Success"));
const B2BDashboard = lazy(() => import("@/b2b/pages/Dashboard"));
const B2BProducts = lazy(() => import("@/b2b/pages/Products"));
const B2BAddProduct = lazy(() => import("@/b2b/pages/AddProduct"));
const B2BProductDetail = lazy(() => import("@/b2b/pages/ProductDetail"));
const B2BCollections = lazy(() => import("@/b2b/pages/Collections"));
const B2BBuyers = lazy(() => import("@/b2b/pages/Buyers"));
const B2BOrders = lazy(() => import("@/b2b/pages/Orders"));
const B2BOrderDetail = lazy(() => import("@/b2b/pages/OrderDetail"));
const B2BShare = lazy(() => import("@/b2b/pages/Share"));
const B2BAnalytics = lazy(() => import("@/b2b/pages/Analytics"));

// B2B context only needed inside B2B routes; lazy so its bundle/CSS stays off the retail path.
const B2BLayout = lazy(() => import("@/b2b/B2BLayout"));

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <CustomerProvider>
        <BrowserRouter>
          <ScrollToTop />
          <PageTracker />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/schedule-call" element={<ScheduleCall />} />
              <Route path="/login" element={<CustomerLogin />} />
              <Route path="/account" element={<Account />} />
              <Route path="/wishlist" element={<Account />} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/product/new" element={<AdminProductUpload />} />
              <Route path="/admin/product/edit/:id" element={<AdminProductEdit />} />
              <Route path="/admin/video-bookings" element={<AdminVideoBookings />} />
              <Route path="/admin/customers" element={<AdminCustomers />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/overview" element={<AdminOverview />} />

              {/* B2B Wholesale — wrapped in its own provider/CSS layout */}
              <Route element={<B2BLayout />}>
                {/* buyer (private link) */}
                <Route path="/b2b" element={<B2BWholesaleCatalog />} />
                <Route path="/b2b/catalog" element={<B2BWholesaleCatalog />} />
                <Route path="/b2b/catalog/:id" element={<B2BWholesaleProduct />} />
                <Route path="/b2b/share/:code" element={<B2BWholesaleCatalog />} />
                <Route path="/b2b/order" element={<B2BCart />} />
                <Route path="/b2b/order/build" element={<B2BBulkOrder />} />
                <Route path="/b2b/order/info" element={<B2BBuyerInfo />} />
                <Route path="/b2b/order/success" element={<B2BSuccess />} />

                {/* admin console */}
                <Route path="/b2b/admin" element={<B2BDashboard />} />
                <Route path="/b2b/admin/products" element={<B2BProducts />} />
                <Route path="/b2b/admin/products/new" element={<B2BAddProduct />} />
                <Route path="/b2b/admin/products/:id" element={<B2BProductDetail />} />
                <Route path="/b2b/admin/collections" element={<B2BCollections />} />
                <Route path="/b2b/admin/buyers" element={<B2BBuyers />} />
                <Route path="/b2b/admin/orders" element={<B2BOrders />} />
                <Route path="/b2b/admin/orders/:id" element={<B2BOrderDetail />} />
                <Route path="/b2b/admin/share" element={<B2BShare />} />
                <Route path="/b2b/admin/analytics" element={<B2BAnalytics />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </CustomerProvider>
    </div>
  );
}

export default App;
